import i18n from '../../i18n';

/**
 * Multi-file upload — the pure half of the fleet primitive (docs/upload-primitive.md).
 *
 * Everything that decides what happens to a file lives here, so it can be tested without a DOM and
 * reused by any app whose upload endpoint takes one file per request: which files are refused before
 * they leave the browser (U3), which type each file is given (U2), which are duplicates (U4), and how
 * a file moves through staged → uploading → uploaded | failed (U5).
 */

export interface UploadType {
  value: string;
  label: string;
  /** Filename patterns that suggest this type (U2 — see `suggestType` for how overlaps resolve). */
  match?: RegExp[];
}

export interface UploadRules {
  /** Lower-case extensions without the dot — must equal the server's list (pinned by a test). */
  extensions: readonly string[];
  maxBytes: number;
}

export type UploadPhase = 'staged' | 'uploading' | 'uploaded' | 'failed';

export interface QueuedFile {
  key: string;
  file: File;
  type: string | null;
  phase: UploadPhase;
  /** Why the browser refuses to send it at all (U3). Never retried. */
  invalid: string | null;
  /** The name of the document already on the record that this one duplicates (U4). */
  duplicateOf: string | null;
  /** The user chose "Upload anyway" on a duplicate. */
  allowDuplicate: boolean;
  /** The server's reason, when the upload failed (U5). */
  error: string | null;
  /** How many times it has been sent. A file saved after more than one is said to be "on retry" (U8). */
  attempts: number;
}

export type DerivedStatus = 'invalid' | 'duplicate' | 'needs-type' | 'ready' | 'uploading' | 'uploaded' | 'failed';

export function statusOf(q: QueuedFile): DerivedStatus {
  if (q.invalid) return 'invalid';
  if (q.phase !== 'staged') return q.phase;
  if (q.duplicateOf && !q.allowDuplicate) return 'duplicate';
  if (!q.type) return 'needs-type';
  return 'ready';
}

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** U3: refused up front, with the reason a person can act on. Null when the file may be sent. */
export function validateFile(file: File, rules: UploadRules): string | null {
  const ext = extensionOf(file.name);
  if (!ext || !rules.extensions.includes(ext)) {
    return i18n.t('upload.unsupported', { ext: ext ? ` (.${ext})` : '', allowed: rules.extensions.join(', ') });
  }
  if (file.size === 0) return i18n.t('upload.empty');
  if (file.size > rules.maxBytes) {
    return i18n.t('upload.tooLarge', { size: formatBytes(file.size), limit: formatBytes(rules.maxBytes) });
  }
  return null;
}

/**
 * U2: the type a filename suggests, or null — the person chooses, nothing is guessed.
 *
 * - A match lying INSIDE a longer match of another type yields to it: with a `balance` pattern on
 *   balance sheets, "Trial Balance Sep.xlsx" is still a trial balance.
 * - Two SEPARATE matches of different types mean the name is ambiguous ("P&L and Balance Sheet.pdf"
 *   is both), so there is no suggestion rather than a confident wrong one.
 */
export function suggestType(fileName: string, types: readonly UploadType[]): string | null {
  const base = fileName.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ');
  const hits: { value: string; start: number; end: number }[] = [];
  for (const t of types) {
    for (const re of t.match ?? []) {
      const m = re.exec(base);
      if (m) hits.push({ value: t.value, start: m.index, end: m.index + m[0].length });
    }
  }
  const inside = (a: (typeof hits)[number], b: (typeof hits)[number]) =>
    a.value !== b.value && b.start <= a.start && a.end <= b.end && b.end - b.start > a.end - a.start;
  const kept = hits.filter((h) => !hits.some((o) => inside(h, o)));
  const values = new Set(kept.map((h) => h.value));
  return values.size === 1 ? [...values][0] : null;
}

export function fileKey(f: File): string {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

/**
 * Adds files to the queue. The same file dropped twice is added once and said (U4); a file named
 * like a document already on the record is flagged, and is only sent if the person says so.
 */
export function addFiles(
  queue: readonly QueuedFile[],
  files: readonly File[],
  ctx: { rules: UploadRules; types: readonly UploadType[]; existingNames: readonly string[] },
): { queue: QueuedFile[]; notice: string | null } {
  const keys = new Set(queue.map((q) => q.key));
  const existing = new Map(ctx.existingNames.map((n) => [n.toLowerCase(), n]));
  const next = [...queue];
  let repeated = 0;
  for (const file of files) {
    const key = fileKey(file);
    if (keys.has(key)) {
      // U8: a row that FAILED is not "already in the list" — it never reached the record. Dropping the
      // file again re-stages that row (keeping its type and attempt count) instead of refusing it.
      const at = next.findIndex((q) => q.key === key);
      if (at >= 0 && next[at].phase === 'failed') {
        next[at] = { ...next[at], file, phase: 'staged', error: null };
      } else {
        repeated += 1;
      }
      continue;
    }
    keys.add(key);
    next.push({
      key,
      file,
      type: suggestType(file.name, ctx.types),
      phase: 'staged',
      invalid: validateFile(file, ctx.rules),
      duplicateOf: existing.get(file.name.toLowerCase()) ?? null,
      allowDuplicate: false,
      error: null,
      attempts: 0,
    });
  }
  const notice = repeated
    ? i18n.t('upload.repeated', { count: repeated })
    : null;
  return { queue: next, notice };
}

/** A file the endpoint confirmed (U7), as the hand-off notice names it. */
export interface SavedFile {
  name: string;
  retried: boolean;
}

/**
 * U8: the sentence a finished batch leaves behind once its confirmed rows have left the list —
 * "Saved to this review: P&L.pdf, Balance Sheet.pdf (on retry)." Null when nothing was saved.
 */
export function savedNotice(saved: readonly SavedFile[]): string | null {
  if (saved.length === 0) return null;
  const names = saved.map((s) => (s.retried ? i18n.t('upload.savedRetried', { name: s.name }) : s.name)).join(', ');
  return i18n.t('upload.saved', { names });
}

export function patch(queue: readonly QueuedFile[], key: string, change: Partial<QueuedFile>): QueuedFile[] {
  return queue.map((q) => (q.key === key ? { ...q, ...change } : q));
}

/** What a sentence about the queue says — "3 ready · 1 needs a type · 1 refused". */
export function summarize(queue: readonly QueuedFile[]): Record<DerivedStatus, number> {
  const out: Record<DerivedStatus, number> = {
    invalid: 0, duplicate: 0, 'needs-type': 0, ready: 0, uploading: 0, uploaded: 0, failed: 0,
  };
  for (const q of queue) out[statusOf(q)] += 1;
  return out;
}

/**
 * Runs `worker` over `items` with at most `limit` in flight. Resolves when every item has settled;
 * a rejection is the worker's to record — it never stops the others (U5).
 */
export async function runLimited<T>(items: readonly T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const lanes = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      try {
        await worker(item);
      } catch {
        // recorded by the worker
      }
    }
  });
  await Promise.all(lanes);
}
